import axios from "axios";
import { ssoConfig } from "../global/utils/ssoConfig";

const AUTH0_STATE_PREFIX = "sso_state_v2:";

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function toBase64Url(value) {
  const bytes = textEncoder.encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function fromBase64Url(value) {
  let base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4;
  if (pad) {
    base64 += "=".repeat(4 - pad);
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return textDecoder.decode(bytes);
}

function generateRandomId() {
  return (
    Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
  );
}

function getStateStorageKey(id) {
  return `${AUTH0_STATE_PREFIX}${id}`;
}

function saveAuth0State(id, data) {
  try {
    localStorage.setItem(getStateStorageKey(id), JSON.stringify(data));
  } catch (error) {
    console.error("Failed to store Auth0 state", error);
  }
}

export function getStoredAuth0State(id) {
  try {
    const raw = localStorage.getItem(getStateStorageKey(id));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.codeVerifier !== "string") {
      localStorage.removeItem(getStateStorageKey(id));
      return null;
    }

    // Expire after 10 minutes
    if (Date.now() - parsed.createdAt > 10 * 60 * 1000) {
      localStorage.removeItem(getStateStorageKey(id));
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("Failed to read Auth0 state", error);
    return null;
  }
}

export function clearStoredAuth0State(id) {
  try {
    localStorage.removeItem(getStateStorageKey(id));
  } catch (error) {
    console.error("Failed to clear Auth0 state", error);
  }
}

function encodeAuth0State(payload) {
  return toBase64Url(JSON.stringify(payload)) + "==";
}

export function decodeAuth0State(encoded) {
  if (!encoded) return null;
  try {
    const serialized = fromBase64Url(encoded);
    const parsed = JSON.parse(serialized);
    if (
      !parsed ||
      typeof parsed.id !== "string" ||
      typeof parsed.origin !== "string"
    ) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.error("Failed to decode Auth0 state payload", error);
    return null;
  }
}

function createEncodedState(codeVerifier, provider) {
  const id = generateRandomId();
  saveAuth0State(id, { codeVerifier, createdAt: Date.now(), provider });
  const payload = { id, origin: window.location.origin };
  return { id, encoded: encodeAuth0State(payload) };
}

async function generatePKCE() {
  const array = new Uint8Array(96);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(array);
  } else {
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }

  const codeVerifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");

  let codeChallenge = codeVerifier;

  if (
    typeof crypto !== "undefined" &&
    crypto.subtle &&
    typeof crypto.subtle.digest === "function"
  ) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(codeVerifier);
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      codeChallenge = btoa(String.fromCharCode(...hashArray))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");
    } catch (error) {
      codeChallenge = codeVerifier;
    }
  }

  return { codeVerifier, codeChallenge };
}

export class Auth0SSO {
  name = "auth0";

  async initiateLogin(connection, provider) {
    if (!ssoConfig.domain || !ssoConfig.clientId || !ssoConfig.redirectUri) {
      throw new Error("Auth0 SSO is not properly configured");
    }

    const width = 500;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      "",
      "auth0-login",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      throw new Error("Popup blocked. Please allow popups for this site.");
    }

    try {
      popup.document.write(
        '<p style="font-family:sans-serif;padding:16px;">Preparing single sign-on…</p>'
      );
      popup.document.close();
    } catch (error) {
      // Ignore document write errors
    }

    try {
      const { codeVerifier, codeChallenge } = await generatePKCE();
      const { encoded } = createEncodedState(codeVerifier, provider);

      const gatewayUrl = process.env.REACT_APP_GATEWAY_URL;
      if (!gatewayUrl) {
        throw new Error("Gateway URL is not configured");
      }
      // Match working project behavior and avoid trailing-slash redirect.
      const normalizedGatewayUrl = gatewayUrl.replace(/\/+$/, "");
      const ssoStartUrl = `${normalizedGatewayUrl}/api/sso-start`;

      await axios.post(ssoStartUrl, {
        code: codeChallenge,
        state: encoded,
      });

      const auth0AuthUrl = new URL(`https://${ssoConfig.domain}/authorize`);
      auth0AuthUrl.searchParams.set("client_id", ssoConfig.clientId);
      auth0AuthUrl.searchParams.set("response_type", "code");
      auth0AuthUrl.searchParams.set("scope", ssoConfig.scope);
      auth0AuthUrl.searchParams.set("redirect_uri", ssoConfig.redirectUri);
      auth0AuthUrl.searchParams.set("state", encoded);
      if (connection) {
        auth0AuthUrl.searchParams.set("connection", connection);
      }

      popup.location.replace(auth0AuthUrl.toString());
      window.__ssoPopup = popup;
    } catch (error) {
      popup.close();
      throw error;
    }

    return Promise.resolve();
  }
}

export function getSSOService() {
  if (!ssoConfig.enabled) return null;
  return new Auth0SSO();
}

export const SSO_PROVIDER_STORAGE_KEY = "sso:last-provider";
export const SSO_PROVIDER_CODE_KEY = "sso:code";

export function clearAllAuth0States() {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(AUTH0_STATE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error("Failed to clear all Auth0 states", error);
  }
}
